import { createElement, CSSProperties } from 'react';

import '@mui/lab/themeAugmentation';
import type { DataGridProps } from '@mui/x-data-grid';

import { CHECKBOX_CHECK_PATH } from '../../components/checkbox/check-path';
import {
  alpha,
  createTheme,
  darken,
  lighten,
} from '../../components/styles/styles';

declare module '@mui/material/styles' {
  interface Theme {
    colors: {
      gold: string;
      orange: string;
      gradients: {
        blue1: string;
        blue2: string;
        blue3: string;
        blue4: string;
        blue5: string;
        orange1: string;
        orange2: string;
        orange3: string;
        purple1: string;
        purple3: string;
        pink1: string;
        pink2: string;
        green1: string;
        green2: string;
        black1: string;
        black2: string;
        red: string;
      };
      datePicker: {
        chosenDay: string;
        currentDay: string;
      };
      shadows: {
        success: string;
        error: string;
        primary: string;
        warning: string;
        info: string;
        buttons?: string;
        labels?: string;
        timeCards?: string;
        inputs?: string;
        actionButton?: string;
        actionButton2?: string;
        secondaryButton?: string;
        switchButton: string;
        switchThumb?: string;
        primaryButton?: string;
        tertiaryButton?: string;
        defaultAvatar?: string;
        timeCardsSmallTable: string;
        punchTable: string;
        menuItemBgActive: string;
        layout: string;
      };
      border: {
        menuButton: string;
        actionButton: string;
        sortButton: string;
        profileTitle: string;
        primaryButton: string;
        tableLight: string;
        neutral: string;
      };
      alpha: {
        white: {
          5: string;
          10: string;
          30: string;
          50: string;
          70: string;
          100: string;
        };
        trueWhite: {
          5: string;
          10: string;
          30: string;
          50: string;
          70: string;
          100: string;
        };
        black: {
          5: string;
          10: string;
          30: string;
          50: string;
          70: string;
          100: string;
        };
        gray: {
          5: string;
          10: string;
          30: string;
          50: string;
          70: string;
          100: string;
        };
      };
      secondary: {
        main: string;
        light: string;
        lighter: string;
        dark: string;
        border: string;
        text?: string;
      };
      primary: {
        main: string;
        light: string;
        lighter: string;
        dark: string;
        border: string;
        text?: string;
      };
      success: {
        main: string;
        light: string;
        lighter: string;
        dark: string;
        border: string;
        text?: string;
      };
      warning: {
        main: string;
        light: string;
        lighter: string;
        dark: string;
        border: string;
        text?: string;
      };
      error: {
        main: string;
        light: string;
        lighter: string;
        dark: string;
        border: string;
        text?: string;
      };
      info: {
        main: string;
        light: string;
        lighter: string;
        dark: string;
        border: string;
        text?: string;
      };
    };
    general?: {
      reactFrameworkColor: CSSProperties['color'];
      borderRadiusSm: string;
      borderRadius: string;
      borderRadiusLg: string;
      borderRadiusXl: string;
      bodyBg: string;
    };
    borderRadius?: {
      header: string;
      borderRadiusXSmall: string;
      borderRadiusMini: string;
      borderRadiusSmall: string;
      timeCardLabel: string;
      borderRadiusLg: string;
      borderRadiusXXl: string;
      borderRadiusXl: string;
    };
    employee?: {
      laborLevelBtnClicked: string;
    };
    background: {
      lightGrey: string;
      lightGrey2: string;
      lightGrey3: string;
      darkGrey: string;
      darkGrey2: string;
      darkGrey3: string;
      lightPurple: string;
      surfaceLight: string;
      surfaceDark: string;
      defaultAvatar: string;
      grayedButton: string;
      surface: string;
      chosenItem: string;
      white: string;
      menuIcon: string;
      scrollbar: string;
      switchNegative: string;
      info: string;
      warning: string;
      menuItemBgActive: string;
      errorDot: string;
      divider: string;
      iconLightGray: string;
      secondaryButton: string;
      primaryButton: string;
      switchButton: string;
    };
    border?: Record<string, string>;
    custom?: Record<string, string>;
    spacingLevels?: {
      extraSmall: string;
      xSmall: string;
      small: string;
      smallPlus: string;
      medium: string;
      large: string;
      xlarge: string;
      xxlarge: string;
      xxxlarge: string;
    };
    text: {
      darkText: string;
      primary: string;
      placeholder: string;
      secondary: string;
      secondary2: string;
      disabled: string;
      disabled2: string;
      hint: string;
      warning: string;
      avatar: string;
      validation: string;
      mainText: string;
      subText: string;
      subText2: string;
      info: string;
      inactive: string;
      iconMuted: string;
      sectionSubtitle: string;
    };
    sidebar: {
      background: CSSProperties['color'];
      boxShadow: CSSProperties['color'];
      width: string;
      textColor: CSSProperties['color'];
      dividerBg: CSSProperties['color'];
      menuItemColorActive: CSSProperties['color'];
      menuItemBg: CSSProperties['color'];
      menuItemBgActive: CSSProperties['color'];
      menuItemIconColor: CSSProperties['color'];
      menuItemIconColorActive: CSSProperties['color'];
      menuItemHeadingColor: CSSProperties['color'];
    };
    header: {
      height: string;
      background: CSSProperties['color'];
      boxShadow: CSSProperties['color'];
      textColor: CSSProperties['color'];
    };
    colors2: string;
  }

  interface ThemeOptions {
    colors: {
      gold?: string;
      orange?: string;
      gradients?: {
        blue1: string;
        blue2: string;
        blue3: string;
        blue4: string;
        blue5: string;
        orange1: string;
        orange2: string;
        orange3: string;
        purple1: string;
        purple3: string;
        pink1: string;
        pink2: string;
        green1: string;
        green2: string;
        black1: string;
        black2: string;
        red: string;
      };
      datePicker: {
        chosenDay: string;
        currentDay: string;
      };
      shadows: {
        success: string;
        error: string;
        primary: string;
        warning: string;
        info: string;
        labels?: string;
        inputs?: string;
        timeCards?: string;
        actionButton?: string;
        actionButton2?: string;
        secondaryButton?: string;
        switchButton: string;
        switchThumb?: string;
        primaryButton?: string;
        tertiaryButton?: string;
        tooltip: string;
        defaultAvatar: string;
        timeCardsSmallTable: string;
        punchTable: string;
        menuItemBgActive: string;
        layout: string;
      };
      border: {
        menuButton: string;
        actionButton: string;
        sortButton: string;
        profileTitle: string;
        primaryButton: string;
        tableLight: string;
        neutral: string;
      };
      alpha: {
        white: {
          5: string;
          10: string;
          30: string;
          50: string;
          70: string;
          100: string;
        };
        trueWhite: {
          5: string;
          10: string;
          30: string;
          50: string;
          70: string;
          100: string;
        };
        black: {
          5: string;
          10: string;
          30: string;
          50: string;
          70: string;
          100: string;
        };
        gray: {
          5: string;
          10: string;
          30: string;
          50: string;
          70: string;
          100: string;
        };
      };
      secondary: {
        main: string;
        light: string;
        lighter: string;
        dark: string;
        border: string;
        text?: string;
      };
      primary: {
        main: string;
        light: string;
        lighter: string;
        dark: string;
        border: string;
        text?: string;
      };
      success: {
        main: string;
        light: string;
        lighter: string;
        dark: string;
        border: string;
        text?: string;
      };
      warning: {
        main: string;
        light: string;
        lighter: string;
        dark: string;
        border: string;
        text?: string;
      };
      error: {
        main: string;
        light: string;
        lighter: string;
        dark: string;
        border: string;
        text?: string;
      };
      info: {
        main: string;
        light: string;
        lighter: string;
        dark: string;
        border: string;
        text?: string;
      };
    };

    general?: {
      reactFrameworkColor: CSSProperties['color'];
      borderRadiusSm: string;
      borderRadius: string;
      borderRadiusLg: string;
      borderRadiusXl: string;
      bodyBg: string;
    };
    sidebar?: {
      background: CSSProperties['color'];
      boxShadow: CSSProperties['color'];
      width: string;
      textColor: CSSProperties['color'];
      dividerBg: CSSProperties['color'];
      menuItemColorActive: CSSProperties['color'];
      menuItemBg: CSSProperties['color'];
      menuItemBgActive: CSSProperties['color'];
      menuItemIconColor: CSSProperties['color'];
      menuItemIconColorActive: CSSProperties['color'];
      menuItemHeadingColor: CSSProperties['color'];
    };
    borderRadius?: {
      header: string;
      borderRadiusXSmall: string;
      borderRadiusMini: string;
      borderRadiusSmall: string;
      timeCardLabel: string;
      borderRadiusLg: string;
      borderRadiusXXl: string;
      borderRadiusXl: string;
    };
    background?: {
      lightGrey: string;
      lightGrey2: string;
      lightGrey3: string;
      darkGrey: string;
      darkGrey2: string;
      darkGrey3: string;
      lightPurple: string;
      surfaceLight: string;
      surfaceDark: string;
      defaultAvatar: string;
      grayedButton: string;
      surface: string;
      chosenItem: string;
      white: string;
      menuIcon: string;
      scrollbar: string;
      switchNegative: string;
      menuItemBgActive: string;
      errorDot: string;
      divider: string;
      iconLightGray: string;
      info: string;
      warning: string;
      layout: string;
      secondaryButton: string;
      primaryButton: string;
    };
    border?: Record<string, string>;
    custom?: Record<string, string>;
    spacingLevels?: {
      extraSmall: string;
      xSmall: string;
      small: string;
      smallPlus: string;
      medium: string;
      large: string;
      xlarge: string;
      xxlarge: string;
      xxxlarge: string;
    };
    boxShadow?: Record<string, string>;
    text?: {
      primary: string;
      placeholder: string;
      secondary: string;
      secondary2: string;
      disabled: string;
      disabled2: string;
      hint: string;
      warning: string;
      avatar: string;
      validation: string;
      mainText: string;
      subText: string;
      subText2: string;
      info: string;
      inactive: string;
      iconMuted: string;
      sectionSubtitle: string;
    };
    employee?: {
      laborLevelBtnClicked: string;
    };
    header?: {
      height: string;
      background: CSSProperties['color'];
      boxShadow?: CSSProperties['color'];
      textColor: CSSProperties['color'];
    };
  }

  interface ComponentsPropsList {
    MuiDataGrid: DataGridProps;
  }

  interface Components {
    MuiDataGrid?: {
      defaultProps?: Partial<ComponentsPropsList['MuiDataGrid']>;
      styleOverrides?: any;
      variants?: any;
    };
  }
}

// The thin chevron of a dropdown being edited in a table cell (MUI passes its
// MuiSelect-icon / iconOpen classes). Built here like the checkbox tick, so
// the theme imports no component. Same drawing as GridEditChevron.
const GridEditChevronIcon = ({ className }: { className?: string }) =>
  createElement(
    'svg',
    {
      className,
      viewBox: '0 0 24 24',
      'aria-hidden': true,
      fill: 'none',
      stroke: 'currentColor',
      strokeWidth: 1.8,
      strokeLinecap: 'round',
      strokeLinejoin: 'round',
    },
    createElement('path', { d: 'm6 9 6 6 6-6' })
  );

const uiColors = {
  brandNavy: '#0F1830',
  brandIndigo: '#31447C',
  // Tight on purpose: a wider shadow gets cut off by flush wrappers and shows
  // as grey squares at the card corners.
  cardShadow: '0 1px 3px rgba(15, 24, 48, 0.08)',
  cardDivider: '#F0F2F6',
  tableTitleDivider: '#E6EAF1',
  tableHeaderLine: '#E2E6ED',
  tableRowLine: '#EBEEF3',
  punchLineDivider: '#F3F4F7',
  // Time card days just outside the pay period.
  outsidePeriodRowBg: '#D9DEE6',
  outsidePeriodRowLine: 'rgba(15, 24, 48, 0.10)',
  timecardHolidayOutline: '#008000',
  timecardPaidElsewhereBg: '#FFFF00',
  timecardAddedHoursBg: '#ADD8E6',
  timecardOtherDepartmentBg: '#FFFF00',
  // Punch text colours come from the server; these are the Legend samples.
  timecardPunchAdded: '#FF0000',
  timecardPunchOvernight: '#800000',
  timecardPunchRemote: '#0000FF',
  timecardPunchMobile: '#800080',
  tableHeaderText: '#8A93A8',
  pinnedColumnLine: '#E8EBF1',
  controlBorder: '#D5DCEB',
  iconButton: '#4A5578',
  iconButtonHover: '#F1F3F8',
  dialogShadow: '0 24px 60px rgba(15, 24, 48, 0.28)',
  dialogFooterLine: '#ECEFF4',
  windowedDialogShadow:
    '0 0 0 1px rgba(15, 24, 48, 0.07), 0 10px 24px rgba(15, 24, 48, 0.1), 0 24px 56px rgba(15, 24, 48, 0.16)',
  windowedHeaderLine: '#EEF0F5',
  hoverBg: '#F6F8FB',
  shellPageBg: '#EDF0F6',
  shellNavText: '#C9D1E6',
  shellNavChevron: '#8C97B5',
  shellNavHoverBg: 'rgba(255, 255, 255, 0.06)',
  shellNavActiveBg: 'rgba(255, 255, 255, 0.12)',
  shellNavActiveText: 'rgba(255, 255, 255, 0.75)',
  // A soft light behind the logo: its blue reads poorly on the navy bar.
  shellLogoGlow:
    'radial-gradient(closest-side, rgba(255, 255, 255, 0.11) 0%, rgba(255, 255, 255, 0.04) 62%, rgba(255, 255, 255, 0) 100%)',
  shellLogoGlowSmall:
    'radial-gradient(closest-side, rgba(255, 255, 255, 0.13) 0%, rgba(255, 255, 255, 0.04) 65%, rgba(255, 255, 255, 0) 100%)',
  shellCrumbDivider: 'rgba(255, 255, 255, 0.2)',
  shellCrumbText: '#AEB8D3',
  shellAvatarBg: '#2A3658',
  shellMenuShadow:
    '0 0 0 1px rgba(15, 24, 48, 0.06), 0 18px 44px rgba(15, 24, 48, 0.18)',
  shellAccountMenuShadow:
    '0 0 0 1px rgba(15, 24, 48, 0.06), 0 18px 48px rgba(15, 24, 48, 0.22)',
  shellMenuItemText: '#3D4A6B',
  shellMenuItemHoverBg: '#F5F7FB',
  shellMenuItemCurrentBg: '#EEF3FE',
  shellMenuItemCurrentText: '#1F45C2',
  shellMutedText: '#56627F',
  shellMenuIcon: '#7A86A6',
  shellMenuDivider: '#E6EAF4',
  shellRoleBg: '#EEF1F8',
  shellProfileBg: '#F5F7FB',
  shellProfileOpenBg: '#ECF0F8',
  shellSectionLine: '#E3E6EE',
  shellSectionLabel: '#8590AB',
  shellSectionDivider: '#E3E7EF',
  shellSectionTrackBg: '#EEF1F7',
  shellSectionTabText: '#4A5675',
  payDateCommentDot: '#7B9AF3',
  // Pay date comment plus other hours comments.
  payDateMixedCommentDot: '#6CC49A',
  // Other hours or placeholder comment only.
  otherHoursCommentDot: '#7D879C',
  shellSectionTabShadow: '0 1px 3px rgba(15, 24, 48, 0.12)',
  segmentActiveShadow:
    '0 1px 3px rgba(15, 24, 48, 0.14), 0 0 0 1px rgba(15, 24, 48, 0.04)',
  shellSideIcon: '#6B7699',
  shellScrim: 'rgba(15, 24, 48, 0.32)',
  listPanelShadow: '4px 0 16px rgba(15, 24, 48, 0.06)',
  listHeaderLine: '#E0E5F0',
  listFooterLine: '#E3E8F2',
  listButtonHoverBg: '#F4F6FB',
  listAvatarBg: '#E3E8F3',
  listRowSelectedBg: '#EAF0FE',
  listGroupAccent1: '#31447C',
  listGroupAccent2: '#9AA8D6',
  listGroupAccent3: '#BCC6E6',
  listGroupAccent4: '#D3DAEE',
  listGroupSubLabel: '#5B6788',
  listGroupRailActive: '#9DB5F7',
  listStatusActive: '#22A06B',
  listStatusInactive: '#E5484D',
  statusActiveText: '#16794C',
  statusInactiveDot: '#C3C9D6',
  requestText: '#8A5A00',
  requestBg: '#FCF1DC',
  // StatusTag. Other tones reuse the tokens above:
  // success = statusActiveText / listStatusActive / headerActiveBg,
  // neutral text/dot = shellSideIcon / statusInactiveDot,
  // danger = messageDangerIcon / listStatusInactive / messageDangerBg,
  // info text = brandIndigo, warning text/tint = requestText / requestBg.
  statusTagNeutralBg: '#EEF0F5',
  statusTagInfoDot: '#7B9AF3',
  statusTagInfoBg: '#EEF1FB',
  statusTagWarningDot: '#E0A100',
  // Pending app request dot (Timecards tab, pay date).
  pendingDot: '#F2B33D',
  requestIcon: '#C27C0E',
  filterCountBg: '#E3EAFD',
  filterCheckboxRing: '#C9D0DC',
  filterSwitchOff: '#D5DAE4',
  filterDisabledText: '#A3ABBE',
  filterNoOptions: '#B4BBCB',
  headerCameraShadow: '0 1px 3px rgba(15, 24, 48, 0.25), 0 0 0 1px #E1E6F0',
  headerInfoText: '#5E6880',
  headerActiveBg: '#E7F5EC',
  fieldHoverBg: '#F1F4FA',
  fieldFocusRing: '#E8EEFE',
  fieldPlaceholder: '#B3BAC8',
  checkboxCheckedRing: '#A9BEF6',
  toolCardHoverBorder: '#C9D6FB',
  scrollThumb: '#7D879E',
  undoMenuShadow:
    '0 0 0 1px rgba(15, 24, 48, 0.06), 0 14px 36px rgba(15, 24, 48, 0.16)',
  shellSetupNavBg: '#F6F8FC',
  shellSetupNavBorder: '#E1E6F0',
  shellSetupNavActiveShadow:
    '0 1px 2px rgba(15, 24, 48, 0.1), 0 0 0 1px #E1E6F0',
  menuPanelBorder: '#D9DFEC',
  columnsPopoverShadow: '0 0 0 1px #E3E8F2, 0 12px 32px rgba(15, 24, 48, 0.16)',
  columnsRowHoverBg: '#F4F6FA',
  checkboxLockedBg: '#F3F5F8',
  checkboxLockedRing: '#E1E5EC',
  columnsLockedHint: '#A0A6B4',
  menuPanelShadow: '0 14px 32px rgba(15, 24, 48, 0.16)',
  menuDangerHoverBg: '#FDF1F0',
  payPeriodOpen: '#12804A',
  controlShadow: '0 1px 2px rgba(15, 24, 48, 0.04)',
  popoverShadow: '0 0 0 1px #E3E7EF, 0 14px 36px rgba(15, 24, 48, 0.16)',
  // In Service filter chip when set to a non-default value.
  filterChipActiveBorder: '#BFD0FA',
  fieldErrorBorder: '#E5A39B',
  fieldErrorRing: '#FBE9E7',
  fieldRequiredMark: '#C0392B',
  stepBg: '#EEF1F6',
  stepText: '#3A4560',
  stepDoneBg: '#E7F7EF',
  stepDoneText: '#12805C',
  stepErrorBg: '#F03E3E',
  copyChipBorder: '#BFCBEA',
  copyChipOnBorder: '#C9D6FA',
  copyChipHoverBg: '#F5F8FF',
  messageWarnBg: '#FFF4E5',
  messageWarnIcon: '#B54708',
  messageDangerBg: '#FDECEC',
  messageDangerIcon: '#B42318',
  dangerButtonBg: '#D92D20',
  dangerButtonHoverBg: '#B42318',
  shellSidePanelShadow:
    '0 0 0 1px rgba(15, 24, 48, 0.06), 0 24px 60px rgba(15, 24, 48, 0.22)',
};

const themeColors = {
  primary: {
    main: '#2f5fed',
    light: '#eaf0fe',
    lighter: '#eaf0fe',
    border: '#EAEDF2', //default border
    dark: '#16213f',
  },
  secondary: {
    main: '#eaf0fe', //light purple
    light: '#c1cad6',
    lighter: '#DCE1E8',
    border: '#ECEFF3',
    dark: '#48576d',
    text: '#353849',
  },

  success: {
    main: '#47D8A4',
    light: '#DAFCF0',
    lighter: '#DAFCF0',
    border: '#aaedd5',
    dark: '#27775a',
    text: '#1E5B45',
  },
  background: {
    lightGrey: '#F3F4F6',
    lightGrey2: '#F9FAFB',
    lightGrey3: '#E5E7EB',
    darkGrey: '#6A7282',
    darkGrey2: '#99A1AF',
    darkGrey3: '#101828',
    lightPurple: '#dce6fd',
    surfaceLight: '#FBFCFE',
    surfaceDark: '#ECEFF3',
    defaultAvatar: 'linear-gradient(180deg, #ECEFF3 0%, #FBFCFE 100%)',
    grayedButton: '#EAEDF2',
    surface: '#F6F8FB',
    chosenItem: '#ECEFF3',
    white: '#FFFFFF',
    menuIcon: '#818898',
    scrollbar: '#E8EBF0',
    switchNegative: '#F2F2F7',
    info: '#DFF7FF',
    warning: '#FFEBC8',
    menuItemBgActive: '#2f5fed',
    errorDot: '#FF6467',
    divider: '#E0E0E0',
    iconLightGray: '#E9E9EA',
    layout: `
    linear-gradient(0deg, #F6F8FB 0%, #F6F8FB 100%),
    radial-gradient(42.84% 44.08% at 27.71% 100%, rgba(255, 255, 255, 0.20) 0%, rgba(255, 255, 255, 0.00) 100%),
    linear-gradient(180deg, #0c1226 1.83%, #16213f 100%)
  `,
    secondaryButton:
      'linear-gradient(180deg, rgba(255, 255, 255, 0.12) 0%, rgba(255, 255, 255, 0) 100%), #FFF',
    primaryButton:
      'linear-gradient(180deg, rgba(255, 255, 255, 0.12) 0%, rgba(255, 255, 255, 0.00) 100%), #2f5fed',
  },
  error: {
    main: '#9E1435',
    light: '#FCE8ED',
    lighter: '#fce8ed',
    border: '#f5b9c7',
    dark: '#7b0f29',
    text: '#9E1435',
  },

  info: {
    main: '#353849',
    light: '#a1e9ff',
    lighter: '#DFF7FF',
    border: '#c0f0ff',
    dark: '#16596d',
    text: '#353849',
  },

  warning: {
    main: '#FFA757',
    light: '#FFF9ED',
    lighter: '#FFF9ED',
    border: '#ffe1ad',
    dark: '#674d1e',
    text: '#6B5020',
  },
  text: {
    darkText: '#364153',
    primary: '#0D0D12',
    placeholder: '#818898',
    secondary: '#596074',
    secondary2: '#353849',
    disabled: '#99A3B2',
    disabled2: '#B3BBC7',
    hint: '#9e9e9e',
    warning: '#6B5020',
    avatar: '#2f5fed',
    validation: '#DF1C4B',
    mainText: '#0D0D12',
    subText: '#596074',
    subText2: '#353849',
    info: '#15576B',
    inactive: '#4A5565',
    iconMuted: '#858688',
    sectionSubtitle: '#667085',
  },
  black: '#0D0D12',
  white: '#ffffff',
  gray: '#808080',
  primaryAlt: '#16213f',
  gold: '#d4a955', // brand accent — logo/"360" only, do not use as an interactive color
  orange: '#f28571', // badge "orange" variant accent
};

const colors = {
  shadows: {
    tooltip: '0px 2px 8px 0px rgba(1, 19, 44, 0.40)',
    labels: '0px 1px 2px 0px rgba(22, 33, 63, 0.08)',
    // timeCards:
    //   '0px 1px 4px 0px rgba(22, 33, 63, 0.08), 0px 0px 0px 1px #ECEFF3',
    inputs: '0px 1px 2px 0px rgba(22, 33, 63, 0.08), 0px 0px 0px 1px #ECEFF3',
    buttons:
      '0px 1px 2px 0px rgba(164, 172, 185, 0.24), 0px 0px 0px 1px rgba(22, 33, 63, 0.08)',
    actionButton:
      '0px 1px 2px 0px rgba(22, 33, 63, 0.50), 0px 0px 0px 1px #2f5fed',
    primaryButton:
      '0px 1px 2px 0px rgba(22, 33, 63, 0.50), 0px 0px 0px 1px #2f5fed',
    actionButton2:
      '0px 1px 2px 0px rgba(164, 172, 185, 0.24), 0px 0px 0px 1px rgba(22, 33, 63, 0.08)',
    secondaryButton:
      '0px 1px 2px 0px rgba(164, 172, 185, 0.24), 0px 0px 0px 1px rgba(22, 33, 63, 0.08)',
    tertiaryButton:
      '0px 1px 2px 0px rgba(22, 33, 63, 0.08), 0px 0px 0px 1px  #ECEFF3',
    errorBoxShadow:
      '0px 1px 2px 0px rgba(150, 19, 44, 0.32), 0px 0px 0px 1px #DF1C4B, 0px 0px 0px 2px #FFF, 0px 0px 0px 3px rgba(223, 28, 75, 0.32)',
    focusBoxShadow:
      '0px 1px 2px 0px rgba(22, 33, 63, 0.40), 0px 0px 0px 1px #2f5fed, 0px 0px 0px 2px #FFF, 0px 0px 0px 3px rgba(47, 95, 237, 0.32)',
    defaultAvatar: '0px 0px 8px 0px #eaf0fe inset',
    menuItemBgActive: '0px 1px 2px 0px #a9c6ff',
    switchButton: `
    0px 9px 3px 0px rgba(139, 140, 160, 0.00),
    0px 6px 2px 0px rgba(139, 140, 160, 0.02),
    0px 3px 2px 0px rgba(139, 140, 160, 0.06),
    0px 1px 1px 0px rgba(139, 140, 160, 0.10),
    0px 0px 1px 0px rgba(139, 140, 160, 0.12)
  `,
    switchThumb: '0 2px 4px 0 rgb(0 35 11 / 20%)',
    timeCards:
      '0px 40px 11px 0px rgba(187, 189, 195, 0.00), 0px 25px 10px 0px rgba(187, 189, 195, 0.01), 0px 14px 9px 0px rgba(187, 189, 195, 0.05), 0px 6px 6px 0px rgba(187, 189, 195, 0.09), 0px 2px 3px 0px rgba(187, 189, 195, 0.10)',
    layout:
      '0px 40px 11px 0px rgba(187, 189, 195, 0.00), 0px 25px 10px 0px rgba(187, 189, 195, 0.01), 0px 14px 9px 0px rgba(187, 189, 195, 0.05), 0px 6px 6px 0px rgba(187, 189, 195, 0.09), 0px 2px 3px 0px rgba(187, 189, 195, 0.10)',
    timeCardsSmallTable: '0px 4px 10px 0px rgba(0, 0, 0, 0.25)',
    punchTable:
      '0px 1px 2px 0px rgba(22, 33, 63, 0.08), 0px 0px 0px 3px  #eaf0fe',
  },
  border: {
    menuButton: '#ECEFF3',
    actionButton: '#2f5fed',
    sortButton: '#c7d6fb',
    profileTitle: '#E8EBF0',
    primaryButton: '#2f5fed',
    tableLight: '#f1f1f1',
    neutral: '#cccccc',
  },
  layout: {
    general: {
      bodyBg: '#F6F8FB',
    },
    sidebar: {
      background: '#F6F8FB',
      textColor: themeColors.white,
      dividerBg: '#ECEFF3',
      menuItemColorActive: '#FFFFFF',
      menuItemBg: themeColors.white,
      menuItemBgActive: '#2f5fed',
      menuItemIconColor: lighten(themeColors.secondary.main, 0.3),
      menuItemIconColorActive: themeColors.primary,
      menuItemHeadingColor: darken(themeColors.secondary.main, 0.3),
    },
  },
  alpha: {
    white: {
      5: alpha(themeColors.white, 0.02),
      10: alpha(themeColors.white, 0.1),
      30: alpha(themeColors.white, 0.3),
      50: alpha(themeColors.white, 0.5),
      70: alpha(themeColors.white, 0.7),
      100: themeColors.white,
    },
    trueWhite: {
      5: alpha(themeColors.white, 0.02),
      10: alpha(themeColors.white, 0.1),
      30: alpha(themeColors.white, 0.3),
      50: alpha(themeColors.white, 0.5),
      70: alpha(themeColors.white, 0.7),
      100: themeColors.white,
    },
    black: {
      5: alpha(themeColors.black, 0.02),
      10: alpha(themeColors.black, 0.1),
      30: alpha(themeColors.black, 0.3),
      50: alpha(themeColors.black, 0.5),
      70: alpha(themeColors.black, 0.7),
      100: themeColors.black,
    },
  },
  gold: themeColors.gold,
  orange: themeColors.orange,
};

const LightTheme = createTheme({
  colors: {
    gold: colors.gold,
    orange: colors.orange,
    datePicker: {
      chosenDay: themeColors.primary.main,
      currentDay: themeColors.primary.light,
    },
    shadows: {
      labels: colors.shadows.labels,
      timeCards: colors.shadows.timeCards,
      inputs: colors.shadows.inputs,
      defaultAvatar: colors.shadows.defaultAvatar,
      timeCardsSmallTable: colors.shadows.timeCardsSmallTable,
      punchTable: colors.shadows.punchTable,
      menuItemBgActive: colors.shadows.menuItemBgActive,
      actionButton: colors.shadows.actionButton,
      actionButton2: colors.shadows.actionButton2,
      success: colors.shadows.labels,
      error: colors.shadows.labels,
      primary: colors.shadows.labels,
      info: colors.shadows.labels,
      warning: colors.shadows.labels,
      tooltip: colors.shadows.tooltip,
      layout: colors.shadows.layout,
      switchButton: colors.shadows.switchButton,
      switchThumb: colors.shadows.switchThumb,
    },
    border: {
      menuButton: colors.border.menuButton,
      actionButton: colors.border.actionButton,
      sortButton: colors.border.sortButton,
      profileTitle: colors.border.profileTitle,
      primaryButton: colors.border.primaryButton,
      tableLight: colors.border.tableLight,
      neutral: colors.border.neutral,
    },
    alpha: {
      white: {
        5: alpha(themeColors.white, 0.02),
        10: alpha(themeColors.white, 0.1),
        30: alpha(themeColors.white, 0.3),
        50: alpha(themeColors.white, 0.5),
        70: alpha(themeColors.white, 0.7),
        100: themeColors.white,
      },
      trueWhite: {
        5: alpha(themeColors.white, 0.02),
        10: alpha(themeColors.white, 0.1),
        30: alpha(themeColors.white, 0.3),
        50: alpha(themeColors.white, 0.5),
        70: alpha(themeColors.white, 0.7),
        100: themeColors.white,
      },
      black: {
        5: alpha(themeColors.black, 0.02),
        10: alpha(themeColors.black, 0.1),
        30: alpha(themeColors.black, 0.3),
        50: alpha(themeColors.black, 0.5),
        70: alpha(themeColors.black, 0.7),
        100: themeColors.black,
      },
      gray: {
        5: alpha(themeColors.gray, 0.02),
        10: alpha(themeColors.gray, 0.1),
        30: alpha(themeColors.gray, 0.3),
        50: alpha(themeColors.gray, 0.5),
        70: alpha(themeColors.gray, 0.7),
        100: themeColors.gray,
      },
    },
    secondary: {
      main: themeColors.secondary.main,
      light: themeColors.secondary.light,
      lighter: themeColors.secondary.lighter,
      border: themeColors.secondary.border,
      dark: themeColors.secondary.dark,
    },
    primary: {
      main: themeColors.primary.main,
      light: themeColors.primary.light,
      lighter: themeColors.primary.lighter,
      border: themeColors.primary.border,
      dark: themeColors.primary.dark,
    },
    success: {
      main: themeColors.success.main,
      light: themeColors.success.light,
      lighter: themeColors.success.lighter,
      border: themeColors.success.border,
      dark: themeColors.success.dark,
      text: themeColors.success.text,
    },
    warning: {
      main: themeColors.warning.main,
      light: themeColors.warning.light,
      lighter: themeColors.warning.lighter,
      border: themeColors.warning.border,
      dark: themeColors.warning.dark,
      text: themeColors.warning.text,
    },
    error: {
      main: themeColors.error.main,
      light: themeColors.error.light,
      lighter: themeColors.error.lighter,
      border: themeColors.error.border,
      dark: themeColors.error.dark,
      text: themeColors.error.text,
    },
    info: {
      main: themeColors.info.main,
      light: themeColors.info.light,
      lighter: themeColors.info.lighter,
      border: themeColors.info.border,
      dark: themeColors.info.dark,
      text: themeColors.info.text,
    },
  },
  general: {
    bodyBg: '#F6F8FB',
    reactFrameworkColor: '#00D8FF',
    borderRadiusSm: '6px',
    borderRadius: '10px',
    borderRadiusLg: '12px',
    borderRadiusXl: '16px',
  },
  borderRadius: {
    header: '0px 8px 0px 0px',
    borderRadiusXSmall: '4px',
    borderRadiusMini: '6px',
    borderRadiusSmall: '8px',
    timeCardLabel: '6px 12px 12px 6px',
    borderRadiusLg: '12px',
    borderRadiusXXl: '24px',
    borderRadiusXl: '16px',
  },
  spacingLevels: {
    extraSmall: '4px',
    xSmall: '6px',
    small: '8px',
    smallPlus: '10px',
    medium: '12px',
    large: '16px',
    xlarge: '20px',
    xxlarge: '24px',
    xxxlarge: '48px',
  },
  boxShadow: {
    secondaryButton:
      '0px 1px 2px 0px rgba(164, 172, 185, 0.24), 0px 0px 0px 1px rgba(22, 33, 63, 0.08)',
    primaryButton:
      '0px 1px 2px 0px rgba(22, 33, 63, 0.50), 0px 0px 0px 1px #2f5fed',
    tertiaryButton:
      '0px 1px 2px 0px rgba(22, 33, 63, 0.08), 0px 0px 0px 1px  #ECEFF3',
  },
  text: {
    primary: themeColors.text.primary,
    secondary: themeColors.text.secondary,
    secondary2: themeColors.text.secondary2,
    disabled: themeColors.text.disabled,
    disabled2: themeColors.text.disabled2,
    placeholder: themeColors.text.placeholder,
    hint: themeColors.text.hint,
    warning: themeColors.text.warning,
    avatar: themeColors.text.avatar,
    validation: themeColors.text.validation,
    mainText: themeColors.text.mainText,
    subText: themeColors.text.subText,
    subText2: themeColors.text.subText2,
    info: themeColors.text.info,
    inactive: themeColors.text.inactive,
    iconMuted: themeColors.text.iconMuted,
    sectionSubtitle: themeColors.text.sectionSubtitle,
  },
  employee: {
    laborLevelBtnClicked: '#2f5fed',
  },
  background: {
    lightGrey: themeColors.background.lightGrey,
    lightGrey2: themeColors.background.lightGrey2,
    lightGrey3: themeColors.background.lightGrey3,
    darkGrey: themeColors.background.darkGrey,
    darkGrey2: themeColors.background.darkGrey2,
    darkGrey3: themeColors.background.darkGrey3,
    lightPurple: themeColors.background.lightPurple,
    surfaceLight: themeColors.background.surfaceLight,
    surfaceDark: themeColors.background.surfaceDark,
    defaultAvatar: themeColors.background.defaultAvatar,
    surface: themeColors.background.surface,
    chosenItem: themeColors.background.chosenItem,
    white: themeColors.background.white,
    grayedButton: themeColors.background.grayedButton,
    menuIcon: themeColors.background.menuIcon,
    scrollbar: themeColors.background.scrollbar,
    switchNegative: '#F2F2F7',
    menuItemBgActive: themeColors.background.menuItemBgActive,
    errorDot: themeColors.background.errorDot,
    divider: themeColors.background.divider,
    iconLightGray: themeColors.background.iconLightGray,
    info: themeColors.background.info,
    warning: themeColors.background.warning,
    layout: themeColors.background.layout,
    secondaryButton: themeColors.background.secondaryButton,
    primaryButton: themeColors.background.primaryButton,
  },
  border: {
    menuButton: colors.border.menuButton,
    actionButton: colors.border.actionButton,
    sortButton: colors.border.sortButton,
    profileTitle: colors.border.profileTitle,
    primaryButton: colors.border.primaryButton,
    drafting: '#FFC107',
    subtleBlack: 'rgba(0, 0, 0, 0.1)',
  },
  // Long-tail one-off colors that don't warrant a strictly-typed slot of
  // their own — every literal color value in the app must live here, not
  // as a local constant in a component file.
  custom: {
    ...uiColors,
    cardBoxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
    badgeGrey: '#e9ebef',
    swatchShadow: '0 0 0 3px rgba(0,0,0,.1)',
    tableHeaderRowFallback: '#fbfbfc',
    softNavyShadow: '0px 1px 4px 0px rgba(22, 33, 63, 0.08)',
    disabledRadioBg: '#f0f0f0',
    iconGalleryCardBorder: '#eee',
    iconGalleryTypeColor: '#666',
    iconGalleryBg: '#f7f7f9',
    iconGalleryColor555: '#555',
    iconGalleryColor444: '#444',
    storyPrimaryLight: '#e3f2fd',
    storyPrimaryMain: '#1976d2',
    storyPrimaryDark: '#115293',
    storyTextPrimary: '#000000',
    storyTextSecondary: '#666666',
    collapseIcon: '#B6BDC7',
    closeThreadBg: '#3E3E41',
    streamChatDarkBg: '#333',
    cellFocusBlue: '#1976d2',
    cellFocusAlpha08: 'rgba(25, 118, 210, 0.08)',
    cellFocusAlpha18: 'rgba(25, 118, 210, 0.18)',
    cellFocusAlpha28: 'rgba(25, 118, 210, 0.28)',
    bulletRed: '#ff4d4d',
    bulletBlue: '#123597',
    bulletGreen: '#3CD500',
    highlightPulseLow: 'rgba(234, 240, 254, 0.4)',
    highlightPulseHigh: 'rgba(234, 240, 254, 0.8)',
    localItemFallbackBg: '#fff8e1',
    stickyAuditButtonShadow: '0px 2px 8px rgba(0, 0, 0, 0.2)',
    notifiedIcon: '#FFBE4C',
    interestedIcon: '#33CFFF',
    drawerTabShadow: 'rgba(159, 162, 191, 0.22)',
    employeesIconBg: '#41C595',
    employeesIconMask: '#D9D9D9',
    chartScheduled: '#FF914C',
    chartMin: '#11C0DE',
    chartActual: '#04BE62',
    chartBudget: '#FF5757',
    legendOptimal: '#8cf7c1',
    legendUnder: '#ffcbc9',
    legendOver: '#7b8bff',
    overUnderBgOptimal: '#e8fef2',
    overUnderBgOver: '#e9e9ff',
    overUnderBgUnder: '#fff3f2',
    circleText: '#344358',
    callOffsBg: '#FEE4E2',
    authPageGradient:
      'linear-gradient(135deg, #E3E9FB 0%, #F8FAFF 55%, #FFFFFF 100%)',
    authCardShadow:
      '0 20px 60px rgba(22, 33, 63, 0.12), 0 8px 24px rgba(22, 33, 63, 0.06)',
    errorMessageKeyword: 'red',
  },
  sidebar: {
    background: colors.layout.sidebar.background,
    textColor: colors.layout.sidebar.textColor,
    dividerBg: colors.layout.sidebar.dividerBg,
    menuItemColorActive: colors.layout.sidebar.menuItemColorActive,
    menuItemBg: colors.layout.sidebar.menuItemBg,
    menuItemBgActive: colors.layout.sidebar.menuItemBgActive,
    menuItemIconColor: colors.layout.sidebar.menuItemIconColor,
    menuItemIconColorActive: colors.layout.sidebar
      .menuItemIconColorActive as any,
    menuItemHeadingColor: colors.layout.sidebar.menuItemHeadingColor,
    boxShadow:
      '2px 0 3px rgba(159, 162, 191, .18), 1px 0 1px rgba(159, 162, 191, 0.32)',
    width: '290px',
  },
  header: {
    height: '80px',
    background: colors.alpha.white[100],
    boxShadow: colors.shadows.inputs,
    textColor: themeColors.secondary.main,
  },
  spacing: 4,
  palette: {
    common: {
      black: colors.alpha.black[100],
      white: colors.alpha.white[100],
    },
    mode: 'light',
    primary: {
      light: themeColors.primary.light,
      main: themeColors.primary.main,
      dark: themeColors.primary.dark,
      contrastText: colors.alpha.white[100],
    },
    secondary: {
      light: themeColors.secondary.light,
      main: themeColors.secondary.main,
      dark: themeColors.secondary.dark,
      contrastText: colors.alpha.white[100],
    },
    error: {
      light: themeColors.error.light,
      main: themeColors.error.main,
      dark: themeColors.error.dark,
      contrastText: colors.alpha.white[100],
    },
    success: {
      light: themeColors.success.light,
      main: themeColors.success.main,
      dark: themeColors.success.dark,
      contrastText: colors.alpha.white[100],
    },
    info: {
      light: themeColors.info.light,
      main: themeColors.info.main,
      dark: themeColors.info.dark,
      contrastText: colors.alpha.white[100],
    },
    warning: {
      light: themeColors.warning.light,
      main: themeColors.warning.main,
      dark: themeColors.warning.dark,
      contrastText: colors.alpha.white[100],
    },
    text: {
      primary: colors.alpha.black[100],
      secondary: colors.alpha.black[70],
      disabled: colors.alpha.black[50],
    },
    background: {
      paper: colors.alpha.white[100],
      default: colors.layout.general.bodyBg,
    },
    action: {
      active: colors.alpha.black[100],
      hover: themeColors.primary.lighter,
      hoverOpacity: 0.1,
      selected: colors.alpha.black[10],
      selectedOpacity: 0.1,
      disabled: '#99A3B2',
      disabledOpacity: 0.38,
      focus: colors.alpha.black[10],
      focusOpacity: 0.05,
      activatedOpacity: 0.12,
    },
    tonalOffset: 0.5,
  },
  zIndex: {
    mobileStepper: 1000,
    fab: 1050,
    speedDial: 1050,
    appBar: 1100,
    drawer: 1200,
    modal: 1300,
    snackbar: 1400,
    tooltip: 1500,
  },
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 960,
      lg: 1280,
      xl: 1840,
    },
  },
  components: {
    // Applies to every MUI checkbox, including DataGrid selection, filters and
    // column menus.
    MuiCheckbox: {
      defaultProps: {
        size: 'medium',
        color: 'primary',
        disableRipple: true,
        icon: createElement('span', { className: 'SbvCheckbox-box' }),
        checkedIcon: createElement(
          'span',
          { className: 'SbvCheckbox-box SbvCheckbox-on' },
          createElement(
            'svg',
            { viewBox: '0 0 24 24', 'aria-hidden': true },
            createElement('path', { d: CHECKBOX_CHECK_PATH })
          )
        ),
        indeterminateIcon: createElement(
          'span',
          { className: 'SbvCheckbox-box SbvCheckbox-on' },
          createElement('i', { className: 'SbvCheckbox-bar' })
        ),
      },
      styleOverrides: {
        root: {
          position: 'relative',
          width: 18,
          height: 18,
          padding: 0,
          flexShrink: 0,
          borderRadius: '99px',
          '&:hover::before': {
            content: '""',
            position: 'absolute',
            top: '50%',
            left: '50%',
            width: 35,
            height: 35,
            borderRadius: '99px',
            background: themeColors.background.chosenItem,
            transform: 'translate(-50%, -50%)',
          },
          '&:hover': { backgroundColor: 'transparent' },
          '& .SbvCheckbox-box': {
            position: 'relative',
            zIndex: 1,
            pointerEvents: 'none',
            display: 'grid',
            placeItems: 'center',
            width: 18,
            height: 18,
            borderRadius: '5px',
            background: themeColors.background.white,
            boxShadow: `inset 0 0 0 1.5px ${uiColors.filterCheckboxRing}`,
          },
          '& .SbvCheckbox-on': {
            background: uiColors.listRowSelectedBg,
            color: themeColors.primary.main,
            boxShadow: `inset 0 0 0 1.5px ${uiColors.checkboxCheckedRing}`,
            '& svg': { width: 15, height: 15, fill: 'currentColor' },
          },
          '& .SbvCheckbox-bar': {
            width: 10,
            height: 2,
            borderRadius: '1px',
            background: 'currentColor',
          },
          '&.Mui-disabled': { cursor: 'not-allowed' },
          '&.Mui-disabled .SbvCheckbox-box': { opacity: 0.6 },
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          fontSize: '12px',
          fontWeight: 400,
          lineHeight: '16px',
          color: themeColors.white || '#fff',
          backgroundColor: themeColors.secondary.text,
          borderRadius: '4px',
          boxShadow: colors.shadows?.tooltip,
          wordWrap: 'break-word',
          overflowWrap: 'break-word', // break long words
          display: 'inline-block',
        },
      },
      defaultProps: {
        placement: 'top',
      },
    },
    MuiFormLabel: {
      styleOverrides: {
        root: {
          fontSize: '14px',
          fontStyle: 'normal',
          fontWeight: 400,
          lineHeight: '16px',
          letterSpacing: '-0.28px',
          color: themeColors.text.secondary2,
        },
        asterisk: {
          color: '#d32f2f',
        },
      },
    },
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          // Standard properties (Chrome, Firefox) plus the WebKit rules for Safari.
          '*': {
            scrollbarWidth: 'thin',
            scrollbarColor: `${uiColors.scrollThumb} transparent`,
          },
          '*::-webkit-scrollbar': {
            width: '8px',
            height: '8px',
          },
          '*::-webkit-scrollbar-track': {
            backgroundColor: 'transparent',
          },
          '*::-webkit-scrollbar-thumb': {
            backgroundColor: uiColors.scrollThumb,
            borderRadius: '10px',
            border: '2px solid transparent',
            backgroundClip: 'content-box',
          },
          '*::-webkit-scrollbar-thumb:hover': {
            backgroundColor: uiColors.scrollThumb,
          },
          fontFamily: 'Inter',
          fontSize: '14px',
          fontStyle: 'normal',
          fontWeight: '400',
          lineHeight: '16px',
          letterSpacing: '-0.28px',
          '&:has(.MuiDialog-root) .sbv-overlay-loader': {
            display: 'none',
          },
        },
      },
    },
    MuiBackdrop: {
      styleOverrides: {
        root: {
          backgroundColor: alpha(darken(themeColors.primaryAlt, 0.4), 0.2),
          // Popovers and menus use an invisible backdrop; only dialogs dim.
          '&.MuiBackdrop-invisible': { backgroundColor: 'transparent' },
        },
      },
    },

    MuiFormHelperText: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          marginLeft: 8,
          marginRight: 8,
          color: themeColors.text.secondary,
          fontSize: '12px',
          fontWeight: '400',
          lineHeight: '16px',
          '&.Mui-error': {
            color: themeColors.text.secondary, // Or whatever color you need
          },
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        root: {
          color: themeColors.secondary.text,
          paddingTop: '3px',
          '&.MuiInputBase-sizeSmall': {
            fontSize: '13px',
          },
          '& .MuiSelect-icon': {
            marginTop: '0px',
          },
        },
        iconOutlined: {
          color: colors.alpha.black[50],
          fontSize: '20px',
        },
        icon: {
          top: 'calc(50% - 10px)',
        },
      },
    },
    MuiFormControlLabel: {
      styleOverrides: {
        root: {
          '&:has(> .MuiCheckbox-root)': {
            minHeight: 36,
            marginLeft: 0,
            gap: '12px',
            '& .MuiFormControlLabel-label': {
              fontSize: '14px',
              color: uiColors.brandNavy,
            },
          },
          fontSize: '14px',
          fontStyle: 'normal',
          fontWeight: 400,
          lineHeight: '16px',
          letterSpacing: '-0.28px',
          '&.Mui-disabled .MuiFormControlLabel-label': {
            color: themeColors.text.primary,
          },
        },
        label: {
          fontSize: '14px',
          fontStyle: 'normal',
          fontWeight: 400,
          lineHeight: '16px',
          letterSpacing: '-0.28px',
          color: themeColors.text.primary,
        },
      },
    },
    MuiFormControl: {
      styleOverrides: {
        root: {
          '& .MuiInputLabel-sizeSmall': {
            fontSize: 'small',
          },
          fontSize: '14px',
          fontStyle: 'normal',
          fontWeight: 400,
          lineHeight: '16px',
          letterSpacing: '-0.28px',
          color: themeColors.text.primary,
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          border: `none`,
          borderRadius: '8px',
          gap: '8px',
          alignItems: 'center',
          boxShadow: colors.shadows.inputs,
          marginLeft: '1px',
          color: themeColors.secondary.text,
          lineHeight: '16px',
          fontWeight: 400,
          fontSize: '14px',
          height: '32px',
          display: 'flex',
          '& .MuiInputAdornment-sizeSmall': {
            '.MuiSvgIcon-root': {
              fontSize: '20px',
              color: themeColors.text.disabled,
            },
          },
          '&.Mui-disabled': {
            backgroundColor: themeColors.background?.surface,
            border: 'none',
            color: themeColors.text?.disabled,
          },
          '&.Mui-error': {
            color: themeColors.error.text,
            boxShadow: colors.shadows.errorBoxShadow,
          },
          '& input:-webkit-autofill, & input:-webkit-autofill:hover, & input:-webkit-autofill:focus, & input:-webkit-autofill:active':
            {
              WebkitBoxShadow: '0 0 0 100px transparent inset',
              WebkitTextFillColor: themeColors.secondary.text,
              caretColor: themeColors.secondary.text,
              transition:
                'background-color 600000s ease-in-out 0s, color 600000s ease-in-out 0s',
            },
        },
        sizeSmall: {
          height: '32px',

          fieldset: {
            borderRadius: 10,
          },
        },
        multiline: {
          height: 'auto',
          minHeight: '66px',
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        input: {
          height: '32px',
          boxSizing: 'border-box',
          paddingInlineStart: '8px',
          '&::placeholder': {
            color: themeColors.text.disabled,
            opacity: 1,
          },
        },
        root: {
          height: '32px',
          '&.Mui-disabled .MuiOutlinedInput-notchedOutline': {
            border: 'none',
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            border: `1px solid ${themeColors.primary.main}`,
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            boxShadow: colors.shadows.focusBoxShadow,
          },
          '&.Mui-disabled:hover .MuiOutlinedInput-notchedOutline': {
            border: 0,
            color: themeColors.text.disabled,
          },
          '&.Mui-disabled': {
            border: 'none',
          },
        },
        multiline: {
          height: 'auto',
          minHeight: '66px',
        },
        notchedOutline: {
          border: 0,
        },
      },
    },
    ...{
      MuiPickersInputBase: {
        styleOverrides: {
          root: {
            color: themeColors.text.secondary2,
            fontSize: '14px',
            height: '32px',
            border: 'none',
            boxShadow: colors.shadows.inputs,
            paddingLeft: '10px !important',
            paddingRight: '14px !important',

            '& .MuiSvgIcon-root': {
              fontSize: 20,
            },
            '& fieldset': { border: 'none' },
            '&:hover': {
              boxShadow: `0px 1px 2px 0px rgba(22, 33, 63, 0.08), 0px 0px 0px 1px ${themeColors.primary.main}`,
              '& fieldset': {
                borderColor: '#dfe1e7',
              },
            },

            '& svg': {
              color: `${themeColors.text.disabled} !important`,
            },

            '&.Mui-disabled': {
              backgroundColor: '#F6F8FB !important',
              boxShadow: colors.shadows.inputs,
              '& fieldset': {
                border: 'none',
              },
            },
            '&.Mui-error': {
              color: themeColors.error.text,
              boxShadow: colors.shadows.errorBoxShadow,
            },
          },
        },
      },
    },
    MuiListSubheader: {
      styleOverrides: {
        colorPrimary: {
          fontWeight: 'bold',
          lineHeight: '40px',
          fontSize: 13,
          background: colors.alpha.black[5],
          color: colors.alpha.black[70],
        },
      },
    },
    MuiCardHeader: {
      styleOverrides: {
        action: {
          marginTop: -5,
          marginBottom: -5,
        },
        title: {
          fontSize: 15,
        },
      },
    },
    MuiRadio: {
      styleOverrides: {
        root: {
          borderRadius: '50px',
          '&.Mui-checked:hover .MuiSvgIcon-root': {
            color: themeColors.white,
          },
          '&:not(.Mui-checked):hover .MuiSvgIcon-root': {
            borderRadius: '50px',
            color: 'transparent',
            background: themeColors.white,
          },
          color: themeColors.background.surfaceDark,
          '& .MuiSvgIcon-root': {
            width: '20px',
            height: '20px',
            transition: 'all 0.2s ease',
          },
          '&.Mui-checked': {
            '& .MuiSvgIcon-root': {
              background: 'linear-gradient(180deg, #2f5fed 0%, #16213f 100%)',
              borderRadius: '50%',
              color: themeColors.white,
              height: '20px',
              width: '20px',
            },
          },
          '&.Mui-checked.Mui-disabled': {
            '& .MuiSvgIcon-root': {
              background: themeColors.secondary.main,
            },
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: '8px',
        },
      },
    },
    MuiAccordion: {
      styleOverrides: {
        root: {
          boxShadow: 'none',

          '&.Mui-expanded': {
            margin: 0,
          },
          '&::before': {
            display: 'none',
          },
        },
      },
    },
    MuiAvatar: {
      styleOverrides: {
        root: {
          fontSize: 14,
          fontWeight: 'bold',
        },
        colorDefault: {
          background: colors.alpha.black[30],
          color: colors.alpha.white[100],
        },
      },
    },
    MuiAvatarGroup: {
      styleOverrides: {
        root: {
          alignItems: 'center',
        },
        avatar: {
          background: colors.alpha.black[10],
          fontSize: 13,
          color: colors.alpha.black[70],
          fontWeight: 'bold',
        },
      },
    },
    MuiListItemAvatar: {
      styleOverrides: {
        alignItemsFlexStart: {
          marginTop: 0,
        },
      },
    },
    MuiPaginationItem: {
      styleOverrides: {
        page: {
          fontSize: 13,
          fontWeight: 'bold',
          transition: 'all .2s',
        },
        textPrimary: {
          '&.Mui-selected': {
            boxShadow: colors.shadows.inputs,
          },
          '&.MuiButtonBase-root:hover': {
            background: colors.alpha.black[5],
          },
          '&.Mui-selected.MuiButtonBase-root:hover': {
            background: themeColors.primary.main,
          },
        },
      },
    },
    MuiButton: {
      defaultProps: {
        disableRipple: true,
      },
      styleOverrides: {
        root: {
          textTransform: 'none',
          paddingLeft: 16,
          paddingRight: 16,
          '.MuiSvgIcon-root': {
            transition: 'all .2s',
            fontSize: '20px',
          },
        },
        endIcon: {
          marginRight: -8,
        },
        containedSecondary: {
          backgroundColor: themeColors.secondary.main,
          color: colors.alpha.white[100],
          border: '1px solid ' + colors.alpha.black[30],
        },
        outlinedSecondary: {
          backgroundColor: colors.alpha.white[100],

          '&:hover, &.MuiSelected': {
            backgroundColor: colors.alpha.black[5],
            color: colors.alpha.black[100],
          },
        },
        sizeSmall: {
          padding: '7px 16px',
          lineHeight: 1.5,
          borderRadius: 10,
        },
        sizeMedium: {
          padding: '8px 20px',
        },
        sizeLarge: {
          padding: '11px 24px',
        },
        textSizeSmall: {
          padding: '7px 12px',
        },
        textSizeMedium: {
          padding: '9px 16px',
        },
        textSizeLarge: {
          padding: '12px 16px',
        },
      },
      variants: [
        {
          props: { variant: 'soft' },
          style: {
            border: `1px solid ${themeColors.background.lightGrey3}`,
            color: themeColors.info.main,
            '&:hover': {
              backgroundColor: colors.alpha.black[5],
            },
            '&.Mui-disabled': {
              opacity: 0.5,
            },
          },
        },
      ],
    },
    MuiButtonBase: {
      defaultProps: {
        disableRipple: false,
      },
      styleOverrides: {
        root: {
          borderRadius: 10,
        },
      },
    },
    MuiToggleButton: {
      defaultProps: {
        disableRipple: true,
      },
      styleOverrides: {
        root: {
          color: themeColors.primary.main,
          background: colors.alpha.white[100],
          transition: 'all .2s',

          '&:hover, &.Mui-selected, &.Mui-selected:hover': {
            color: colors.alpha.white[100],
            background: themeColors.primary.main,
          },
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          padding: 8,

          '& .MuiTouchRipple-root': {
            borderRadius: 8,
          },
        },
        sizeSmall: {
          padding: 4,
        },
      },
    },
    MuiListItemText: {
      styleOverrides: {
        root: {
          margin: 0,
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          '& .MuiTouchRipple-root': {
            opacity: 0.3,
          },
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          background: colors.alpha.black[10],
          border: 0,
          height: 1,
        },
        vertical: {
          height: 'auto',
          width: 1,

          '&.MuiDivider-flexItem.MuiDivider-fullWidth': {
            height: 'auto',
          },
          '&.MuiDivider-absolute.MuiDivider-fullWidth': {
            height: '100%',
          },
        },
        withChildren: {
          '&:before, &:after': {
            border: 0,
          },
        },
        wrapper: {
          background: colors.alpha.white[100],
          fontWeight: 'bold',
          height: 24,
          lineHeight: '24px',
          marginTop: -12,
          color: 'inherit',
          textTransform: 'uppercase',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          padding: 0,
        },
        elevation0: {
          boxShadow: 'none',
        },
        elevation: {
          boxShadow: colors.shadows.inputs,
        },
        elevation2: {
          boxShadow: colors.shadows.inputs,
        },
        elevation24: {
          boxShadow: colors.shadows.inputs,
        },
        outlined: {
          boxShadow: colors.shadows.inputs,
        },
      },
    },
    MuiLink: {
      defaultProps: {
        underline: 'hover',
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: {
          borderRadius: 6,
          height: 6,
        },
      },
    },
    MuiSlider: {
      styleOverrides: {
        root: {
          '& .MuiSlider-valueLabelCircle, .MuiSlider-valueLabelLabel': {
            transform: 'none',
          },
          '& .MuiSlider-valueLabel': {
            borderRadius: 6,
            background: colors.alpha.black[100],
            color: colors.alpha.white[100],
          },
        },
      },
    },
    MuiList: {
      styleOverrides: {
        root: {
          padding: 0,

          '& .MuiListItem-button': {
            transition: 'all .2s',

            '& > .MuiSvgIcon-root': {
              minWidth: 34,
            },

            '& .MuiTouchRipple-root': {
              opacity: 0.2,
            },
          },
          '& .MuiListItem-root.MuiButtonBase-root.Mui-selected': {
            backgroundColor: alpha(themeColors.primary.lighter, 0.4),
          },
          '& .MuiMenuItem-root.MuiButtonBase-root:active': {
            backgroundColor: alpha(themeColors.primary.lighter, 0.4),
          },
          '& .MuiMenuItem-root.MuiButtonBase-root .MuiTouchRipple-root': {
            opacity: 0.2,
          },
        },
        padding: {
          padding: '12px',

          '& .MuiListItem-button': {
            borderRadius: 6,
            margin: '1px 0',
          },
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        root: {
          height: 44,
          minHeight: 44,
          overflow: 'visible',
          width: 'fit-content',
        },
        indicator: {
          borderRadius: '2px 2px 0px 0px',
          border: '1px solid ' + themeColors.primary.main,
        },
      },
    },
    MuiTab: {
      defaultProps: {
        disableRipple: true,
      },
      styleOverrides: {
        root: {
          padding: 0,
          height: 43,
          WebkitTapHighlightColor: 'transparent',
          minHeight: 38,
          borderRadius: 0,
          transition: 'none',
          textTransform: 'capitalize',
          fontFamily: 'Inter',
          fontSize: '14px',
          fontStyle: 'normal',
          fontWeight: 400,
          lineHeight: 'normal',
          color: themeColors.text?.subText,
          '&.MuiButtonBase-root': {
            minWidth: 'auto',
            height: 43,
            paddingLeft: 4,
            paddingRight: 20,
            paddingBottom: 20,
            paddingTop: 16,
            marginRight: 4,
          },
          '&.Mui-selected, &.Mui-selected:hover': {
            zIndex: 5,
            fontWeight: 500,
            transition: 'none',
          },
          '&.Mui-selected': {
            backgroundColor: 'transparent',
            transition: 'none',
          },
          '&:hover': {
            color: colors.alpha.black[100],
            transition: 'none',
          },
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          padding: 0,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        },
        list: {
          overflowY: 'auto',
          minHeight: 0,
          padding: 8,
          minWidth: 200,
          '& .MuiMenuItem-root.MuiButtonBase-root': {
            fontSize: 14,
            marginTop: 1,
            marginBottom: 1,
            transition: 'all .2s',
            color: colors.alpha.black[70],

            '& .MuiTouchRipple-root': {
              opacity: 0.2,
            },

            '&:hover, &:active, &.active, &.Mui-selected': {
              color: colors.alpha.black[100],
              background: alpha(themeColors.primary.lighter, 0.4),
            },
          },
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          padding: 4,
          height: 32,
          background: 'transparent',
          transition: 'all .2s',

          '&:hover, &:active, &.active, &.Mui-selected': {
            color: colors.alpha.black[100],
            background: alpha(themeColors.primary.lighter, 0.4),
          },
          '&.Mui-selected:hover': {
            background: alpha(themeColors.primary.lighter, 0.4),
          },
        },
      },
    },
    MuiListItem: {
      styleOverrides: {
        root: {
          '&.MuiButtonBase-root': {
            color: themeColors.secondary.main,

            '&:hover, &:active, &.active, &.Mui-selected': {
              color: colors.alpha.black[100],
              background: lighten(themeColors.primary.lighter, 0.5),
            },
          },
        },
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        tag: {
          margin: 1,
        },
        root: {
          '.MuiAutocomplete-inputRoot.MuiOutlinedInput-root .MuiAutocomplete-endAdornment':
            {
              right: 14,
            },
        },
        clearIndicator: {
          background: themeColors.error.lighter,
          color: themeColors.error.main,
          marginRight: 8,

          '&:hover': {
            background: themeColors.error.lighter,
            color: themeColors.error.dark,
          },
        },
        popupIndicator: {
          color: colors.alpha.black[50],

          '&:hover': {
            background: themeColors.primary.lighter,
            color: themeColors.primary.main,
          },
        },
      },
    },
    MuiTablePagination: {
      styleOverrides: {
        toolbar: {
          '& .MuiIconButton-root': {
            padding: 8,
          },
        },
        select: {
          '&:focus': {
            backgroundColor: 'transparent',
          },
        },
      },
    },
    MuiToolbar: {
      styleOverrides: {
        root: {
          padding: '0 !important',
          backgroundColor: 'white',
          minHeight: '58px !important',
        },
      },
    },

    MuiTableContainer: {
      styleOverrides: {
        root: {
          border: '1px solid ' + colors.alpha.black[10],
          borderBottomWidth: 0,
        },
      },
    },

    MuiTableRow: {
      styleOverrides: {
        head: {
          background: colors.alpha.black[5],
        },
        root: {
          transition: 'background-color .2s',
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottomColor: colors.alpha.black[10],
          fontSize: 14,
        },
        head: {
          fontSize: 14,
          fontWeight: 'bold',
          color: colors.alpha.black[70],
          backgroundColor: 'white',
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        message: {
          lineHeight: 1.5,
          fontSize: 14,
        },
        standardInfo: {
          color: themeColors.info.main,
        },
        action: {
          color: colors.alpha.black[70],
        },
      },
    },
    MuiSwitch: {
      styleOverrides: {
        root: {
          height: 33,
          overflow: 'visible',

          '& .MuiButtonBase-root': {
            position: 'absolute',
            padding: 6,
            transition:
              'left 150ms cubic-bezier(0.4, 0, 0.2, 1) 0ms,transform 150ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
          },
          '& .MuiIconButton-root': {
            borderRadius: 100,
          },
          '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
            opacity: 0.3,
          },
        },
        thumb: {
          border: '1px solid ' + colors.alpha.black[30],
          boxShadow:
            '0px 9px 14px ' +
            colors.alpha.black[10] +
            ', 0px 2px 2px ' +
            colors.alpha.black[10],
        },
        track: {
          backgroundColor: colors.alpha.black[5],
          border: '1px solid ' + colors.alpha.black[10],
          boxShadow: 'inset 0px 1px 1px ' + colors.alpha.black[10],
          opacity: 1,
        },
        colorPrimary: {
          '& .MuiSwitch-thumb': {
            backgroundColor: colors.alpha.white[100],
          },

          '&.Mui-checked .MuiSwitch-thumb': {
            backgroundColor: themeColors.primary.main,
          },
        },
      },
    },
    MuiStepper: {
      styleOverrides: {
        root: {
          paddingTop: 20,
          paddingBottom: 20,
          background: colors.alpha.black[5],
        },
      },
    },
    MuiStepIcon: {
      styleOverrides: {
        root: {
          '&.MuiStepIcon-completed': {
            color: themeColors.success.main,
          },
        },
      },
    },
    MuiTypography: {
      defaultProps: {
        variantMapping: {
          h1: 'h1',
          h2: 'h2',
          h3: 'div',
          h4: 'div',
          h5: 'div',
          h6: 'div',
          subtitle1: 'div',
          subtitle2: 'div',
          body1: 'div',
          body2: 'div',
        },
      },
      styleOverrides: {
        root: {
          sizeSmaller: {
            fontSize: 12,
          },
          sizeSmall: {
            fontSize: 14,
          },
        },
        gutterBottom: {
          marginBottom: 4,
        },
        paragraph: {
          fontSize: 17,
          lineHeight: 1.7,
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          padding: '18px 22px 14px',
          fontSize: '17px',
          fontStyle: 'normal',
          fontWeight: 700,
          lineHeight: '23.8px',
          letterSpacing: 'normal',
          color: uiColors.brandNavy,
        },
      },
    },
    MuiDialogContent: {
      styleOverrides: {
        root: {
          padding: '18px 22px 16px',
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          fontFamily: 'Inter',
          fontSize: '11px',
          fontStyle: 'normal',
          fontWeight: '400',
          lineHeight: '12px',
        },
      },
    },
    MuiDataGrid: {
      defaultProps: {
        // Built-in edit dropdowns use the inline-edit chevron.
        slotProps: {
          baseSelect: { material: { IconComponent: GridEditChevronIcon } },
        },
      },
      styleOverrides: {
        root: {
          fontFamily:
            '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif, "Apple Color Emoji", "Segoe UI Emoji"',
          border: `1px solid ${themeColors.background.scrollbar}`,
          '& .MuiDataGrid-columnHeaders': {
            borderBottom: `1px solid ${themeColors.background.scrollbar}`,
          },
          '& .MuiDataGrid-columnHeaders .MuiDataGrid-scrollbarFiller': {
            backgroundColor: themeColors.background.surface,
          },
          '& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within':
            {
              outline: 'none',
            },
          '& .MuiDataGrid-columnHeader': {
            backgroundColor: themeColors.background.surface,
            color: themeColors.text.secondary,
          },
          '& .MuiDataGrid-columnHeaders .MuiDataGrid-filler': {
            backgroundColor: themeColors.background.surface,
          },
          '& .MuiDataGrid-virtualScroller': {
            '--DataGrid-t-cell-background-pinned':
              themeColors.background.surface,
          },
          '& .MuiDataGrid-filler--pinnedLeft': {
            borderRight: '1px solid #e0e0e0',
          },
          '& .MuiDataGrid-filler--pinnedRight': {
            borderLeft: '1px solid #e0e0e0',
          },
          '& .MuiDataGrid-columnHeaderTitle': {
            fontSize: '15px',
            color: themeColors.text.primary,
            lineHeight: '16px',
            letterSpacing: '-0.28px',
          },
          '& .MuiDataGrid-cell.MuiDataGrid-cell--editing': {
            display: 'flex',
            alignItems: 'center',
            padding: '0 6px',
            backgroundColor: 'transparent',
            boxShadow: 'none',
            outline: 'none',
          },
          '& .MuiDataGrid-cell.MuiDataGrid-cell--editing:focus, & .MuiDataGrid-cell.MuiDataGrid-cell--editing:focus-within':
            { outline: 'none' },
          '& .MuiDataGrid-cell--editing .MuiFormControl-root': {
            width: '100%',
          },
          '& .MuiDataGrid-cell--editing .MuiInputBase-root, & .MuiDataGrid-editInputCell':
            {
              width: '100%',
              height: 32,
              borderRadius: '7px',
              fontSize: '14px',
              backgroundColor: 'transparent',
              boxShadow: `0 0 0 3px ${uiColors.fieldFocusRing}`,
            },
          '& .MuiDataGrid-editInputCell': {
            border: `1px solid ${themeColors.primary.main}`,
            padding: '0 8px',
            '& input': { padding: 0 },
          },
          '& .MuiDataGrid-cell--editing .MuiOutlinedInput-notchedOutline, & .MuiDataGrid-cell--editing .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline, & .MuiDataGrid-cell--editing .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline':
            {
              border: `1px solid ${themeColors.primary.main}`,
              borderRadius: '7px',
            },
          '& .MuiDataGrid-cell--editing .MuiSelect-select': {
            display: 'flex',
            alignItems: 'center',
            paddingLeft: '8px',
          },
          // The thin chevron (GridEditChevron, set in defaultProps above).
          '& .MuiDataGrid-cell--editing .MuiSelect-icon': {
            width: 14,
            height: 14,
            right: 10,
            top: 'calc(50% - 7px)',
            color: uiColors.tableHeaderText,
          },
          '& .MuiDataGrid-cell--editing .MuiSelect-select .grid-edit-tick': {
            display: 'none',
          },
          '& .MuiDataGrid-cell[data-field="__detail_panel_toggle__"]': {
            overflow: 'visible',
          },
        },
      },
    },
  },
  shape: {
    borderRadius: 10,
  },
  typography: {
    fontFamily:
      '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif, "Apple Color Emoji", "Segoe UI Emoji"',

    h1: {
      fontWeight: 400,
      fontStyle: 'normal',
      fontSize: '32px',
      lineHeight: '36px',
    },
    h2: {
      fontWeight: 400,
      fontStyle: 'normal',
      fontSize: '28px',
      lineHeight: '32px',
    },
    h3: {
      fontWeight: 400,
      fontStyle: 'normal',
      fontSize: '24px',
      lineHeight: '28px',
    },
    h4: {
      fontWeight: 400,
      fontStyle: 'normal',
      fontSize: '20px',
      lineHeight: '24px',
    },
    h5: {
      fontWeight: 400,
      fontStyle: 'normal',
      fontSize: '18px',
      lineHeight: '22px',
    },
    body1: {
      regular: {
        fontSize: '14px',
        fontWeight: 400,
        lineHeight: '16px',
        letterSpacing: '-0.28px',
      },
      medium: {
        fontSize: '14px',
        fontWeight: 500,
        lineHeight: '16px',
        letterSpacing: '-0.28px',
      },
      semiBold: {
        fontSize: '14px',
        fontWeight: 600,
        lineHeight: '16px',
        letterSpacing: '-0.28px',
      },
      bold: {
        fontSize: '14px',
        fontWeight: 700,
        lineHeight: '16px',
        letterSpacing: '-0.28px',
      },
    },

    body2: {
      fontSize: '13px',
      fontWeight: 300,
    },
    button: {
      fontWeight: 500,
    },
    caption: {
      regular: { fontSize: 12, fontWeight: 400, lineHeight: '16px' },
      medium: { fontSize: 12, fontWeight: 500, lineHeight: '16px' },
      semiBold: { fontSize: 12, fontWeight: 600, lineHeight: '16px' },
      bold: { fontSize: 12, fontWeight: 700, lineHeight: '16px' },
    },

    subtitle1: {
      fontSize: '14px',
      color: colors.alpha.black[70],
    },
    subtitle2: {
      fontWeight: 400,
      fontSize: '15px',
      color: colors.alpha.black[70],
    },
    overline: {
      regular: {
        fontSize: '11px',
        fontWeight: 400,
        textTransform: 'uppercase',
        lineHeight: '12px',
        textAlign: 'center',
      },
      medium: {
        fontSize: '11px',
        fontWeight: 500,
        textTransform: 'uppercase',
        lineHeight: '12px',
        textAlign: 'center',
      },
      semiBold: {
        fontSize: '11px',
        fontWeight: 600,
        textTransform: 'uppercase',
        lineHeight: '12px',
        textAlign: 'center',
      },
      bold: {
        fontSize: '11px',
        fontWeight: 700,
        textTransform: 'uppercase',
        lineHeight: '12px',
        textAlign: 'center',
      },
    },
  },
  shadows: [
    'none',
    'none',
    'none',
    'none',
    'none',
    'none',
    'none',
    'none',
    'none',
    'none',
    'none',
    'none',
    'none',
    'none',
    'none',
    'none',
    'none',
    'none',
    'none',
    'none',
    'none',
    'none',
    'none',
    'none',
    'none',
  ],
});

export default LightTheme;
